import React from 'react';

import { ReportContext } from './hooks/use-context';
import type { EventReportData, QuestionData } from './types';

// Helper function to format date from API response
function formatDate(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  return datePart || '';
}

// Helper function to format time from API response
function formatTime(dateString: string): string {
  if (!dateString) return '';
  const parts = dateString.split(' ');
  if (parts.length < 2) return '';
  const [hours, minutes] = parts[1].split(':');
  return `${hours}:${minutes}`;
}

// Helper to extract day (DD) from formatted date (DD/MM/YYYY)
function getDayFromDate(formattedDate: string): string {
  if (!formattedDate) return '';
  const [day] = formattedDate.split('/');
  return day?.padStart(2, '0') || '';
}

// Helper to extract month name (pt-BR) from date string like 'DD/MM/YYYY HH:mm'
function getMonthNameFromDateString(dateString: string): string {
  if (!dateString) return '';
  const [datePart] = dateString.split(' ');
  const parts = (datePart || '').split('/');
  if (parts.length < 2) return '';
  const monthNum = Number(parts[1]);
  const MONTHS = [
    'janeiro',
    'fevereiro',
    'março',
    'abril',
    'maio',
    'junho',
    'julho',
    'agosto',
    'setembro',
    'outubro',
    'novembro',
    'dezembro',
  ];
  if (!Number.isFinite(monthNum) || monthNum < 1 || monthNum > 12) return '';
  return MONTHS[monthNum - 1];
}

// Helper function to get answer content by question NAME
function getAnswerContentByName(
  questions: QuestionData[],
  questionName: string,
): string {
  const question = questions.find((q) => q.name === questionName);
  if (!question || !question.answers || question.answers.length === 0)
    return '';

  const answer = question.answers[0];
  return answer.option || answer.content || '';
}

export function IzuReportSampleRecord(props: { reportData: EventReportData }) {
  const { reportData } = props;

  // Extract questions from the first section (supports body.items or body[])
  const bodyItems =
    (reportData?.body as any)?.items || (reportData?.body as any);
  const firstItem = Array.isArray(bodyItems) ? bodyItems?.[0] : undefined;
  const questions: QuestionData[] = firstItem?.sections?.[0]?.questions || [];
  const header = reportData?.header;

  // Question NAMES based on form definition
  const QUESTION_NAMES = {
    descricao: 'Descrição',
    responsavelAnalise: 'Responsável pela Análise',
    assinaturaAnalise: 'Assinatura do Responsável pela Análise',
    responsavelEstoque: 'Responsável pelo Estoque',
    assinaturaEstoque: 'Assinatura do Responsável pelo Estoque',
  } as const;

  const eventDateTime =
    header?.event_finished_at ||
    header?.event_date_time ||
    header?.event_created_at ||
    '';
  const date = formatDate(eventDateTime);
  const time = formatTime(eventDateTime);
  const dayFromDate = getDayFromDate(date);
  const monthName = getMonthNameFromDateString(eventDateTime);
  const descricao = getAnswerContentByName(questions, QUESTION_NAMES.descricao);
  const responsavelAnalise = getAnswerContentByName(
    questions,
    QUESTION_NAMES.responsavelAnalise,
  );
  const responsavelEstoque = getAnswerContentByName(
    questions,
    QUESTION_NAMES.responsavelEstoque,
  );
  // Signatures available by name if needed elsewhere
  // const assinaturaAnalise = getAnswerContentByName(questions, QUESTION_NAMES.assinaturaAnalise);
  // const assinaturaEstoque = getAnswerContentByName(questions, QUESTION_NAMES.assinaturaEstoque);

  // Footer data from API
  const elaboradoPor = firstItem?.formulary_last_author || '-';
  const dataElaboracao = firstItem?.formulary_updated_at || '-';
  const aprovadoPor = header?.event_validated_author || '-';
  const dataRevisao = header?.event_validated_at || '-';

  return (
    <ReportContext value={reportData}>
      <div
        style={{
          width: '210mm',
          minHeight: '297mm',
          backgroundColor: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          padding: '20mm',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        {/* Header Section */}
        <div style={{ marginBottom: '20px' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '2px solid #000',
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    width: '20%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                  }}
                >
                  {header?.logo && (
                    <img
                      src={header.logo}
                      alt="Logo"
                      style={{ maxWidth: '100px', height: 'auto' }}
                    />
                  )}
                </td>
                <td
                  style={{
                    width: '50%',
                    padding: '10px',
                    border: '1px solid #000',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  {reportData.header?.event_name}
                </td>
                <td
                  style={{
                    width: '30%',
                    padding: '10px',
                    border: '1px solid #000',
                  }}
                >
                  <div style={{ marginBottom: '5px' }}>PGQ4-00</div>
                  <div>Página: 1 de 1</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Month and Area Section */}
        <div style={{ marginBottom: '10px', fontSize: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>Mês: {monthName}</div>
            {header?.local?.name && <div>Área: {header?.local?.name}</div>}
          </div>
        </div>

        <div
          style={{ fontSize: '14px', lineHeight: '1.5', marginBottom: '10px' }}
        >
          Alimentos: Salmão, tilápia, prego, atum, camarão, patê de salmão
          (Sushibar), Shimeji, kanikon (Cozinha).
        </div>

        {/* Main Data Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            marginBottom: '20px',
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#808080', color: '#fff' }}>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Data
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Descrição
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Responsável pela análise
              </th>
              <th
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 'bold',
                }}
              >
                Recebimento Estoque
              </th>
            </tr>
          </thead>
          <tbody>
            {/* 31 rows (01 to 31); fill answers for the event day, leave others empty */}
            {Array.from({ length: 31 }).map((_, idx) => {
              const dayLabel = String(idx + 1).padStart(2, '0');
              const isEventDay = dayFromDate && dayLabel === dayFromDate;
              return (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={`day_${dayLabel}`}>
                  <td
                    style={{
                      border: '1px solid #000',
                      padding: '8px',
                      textAlign: 'center',
                      height: '24px',
                    }}
                  >
                    {dayLabel}
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>
                    {isEventDay ? descricao : '\u00A0'}
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>
                    {isEventDay ? responsavelAnalise : '\u00A0'}
                  </td>
                  <td style={{ border: '1px solid #000', padding: '8px' }}>
                    {isEventDay ? responsavelEstoque : '\u00A0'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Footer Table */}
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '2px solid #000',
            fontSize: '11px',
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Elaborado por:</strong> {elaboradoPor}
              </td>
              <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '33%',
                }}
              >
                <strong>Aprovado por:</strong> {aprovadoPor}
              </td>
              {/* <td
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  width: '34%',
                }}
              >
                <strong>Revisão:</strong> 003
              </td> */}
            </tr>
            <tr>
              <td style={{ border: '1px solid #000', padding: '8px' }}>
                <strong>Data de elaboração:</strong> {dataElaboracao}
              </td>
              <td style={{ border: '1px solid #000', padding: '8px' }}>
                <strong>Data de aplicação:</strong> {date} {time}
              </td>
              <td style={{ border: '1px solid #000', padding: '8px' }}>
                <strong>Data de aprovação/revisão:</strong> {dataRevisao}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ReportContext>
  );
}
